import Raven from 'raven-js';
/**
 * @description
 * Report an error to sentry.
 *
 * To enable local debugging, set the following option:
 * `localStorage.setItem('SENTRY_DEPLOYED', true);`
 *
 * Per the following Sentry issue, Sentry will automatically combine and throw out messages that
 * share similar stack traces. This makes it impossible to debug local errors since React will
 * cause errors to bubble up through `componentDidCatch` methods, forcing Sentry to effectively log
 * these errors twice. In order to work around this, building a new error in place inside the
 * `componentDidCatch` and attaching the stacktrace as a custom field of that error allows it to
 * reach Sentry.
 * https://github.com/getsentry/sentry-javascript/issues/1249
 *
 * This is a slimmed-down version of reportError from
 * https://git.hubteam.com/HubSpot/conversations-error-reporting/blob/master/static/js/error-reporting/reportError.js
 * See there for fingerprinting and tagging support.
 *
 * @param {Object} options
 * @param {Error} options.error Error to report
 * @param {Object} options.ignoreMeta Ignore rules configuration, i.e expected error codes
 * @returns {string?} Sentry event id
 */
export const reportError = ({
  error,
  tags = {},
  ignoreMeta = {
    errorStatus: []
  },
  extraData = {}
}) => {
  if (error && typeof error === 'object' && 'status' in error && ignoreMeta.errorStatus.some(status => status === error.status)) {
    return null;
  }
  if (error instanceof Error) {
    Raven.captureException(error, {
      tags,
      extra: Object.assign({}, extraData, {
        error
      })
    });
  } else {
    Raven.captureMessage(error.message, {
      tags,
      extra: Object.assign({}, extraData, {
        error
      })
    });
  }
  const lastEventId = Raven.lastEventId();
  console.error(`(Event Id: ${lastEventId})\nError reported with '${error.message}'`);
  return lastEventId;
};